import React from "react";
import {
  Card,
  H1,
  H3,
  Text,
  Button,
  ControlGroup,
  InputGroup,
  Select,
  HTMLSelect,
  Tag,
  Spinner,
  NonIdealState,
  Divider,
  Icon,
  ButtonGroup,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import MultiSelectWrapper from "../../../micro/utils/MultiSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import _ from "lodash";
import { withRouter } from "react-router-dom";
import {
    getStatusMeta as getStatusMetaGlobal,
    toTitleCase as toTitleCaseGlobal,
    getDisplayStatusMeta as getDisplayStatusMetaGlobal,
} from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

class AnesthetistReport extends React.Component {
  state = {
    // Data
    surgeries: [],
    branches: [],

    // Loading states
    isLoading: false,
    isGenerating: false,

    // Filters
    fromDate: new Date(),
    toDate: new Date(),
    selectedBranch: "",
    selectedBranchName: null,
    selectedStatus: ["Confirmed", "Discharged"], // Multiselect status filter, default to confirmed and discharged (capitalized for display)
    selectedIsBilled: "", // Optional isBilled filter

    // Report data
    reportData: [],
    allSurgeries: [],

    // AG Grid state
    gridApi: null,
    gridColumnApi: null,
    quickFilterText: "",
  };

  componentDidMount = async () => {
    await this.loadInitialData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.authStore?.selectedBranchName !== this.props.authStore?.selectedBranchName) {
      const preferredName = this.props.authStore?.selectedBranchName;
      if (!preferredName) {
        this.setState({
          selectedBranch: "",
          selectedBranchName: null,
        });
        return;
      }
      const match = (this.state.branches || []).find(
        (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
      );
      if (match && match._id !== this.state.selectedBranch) {
        this.setState({
          selectedBranch: match._id,
          selectedBranchName: match.name,
        });
      }
    }
  }

  loadInitialData = async () => {
    try {
      this.setState({ isLoading: true });

      // Load branches
      await this.loadBranches();

      // Default branch from global selection if available
      if (Array.isArray(this.state.branches) && this.state.branches.length) {
        const preferredName = this.props.authStore?.selectedBranchName;
        const norm = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();
        const match = preferredName ? this.state.branches.find((b) => norm(b.name) === norm(preferredName)) : null;
        if (match)
          this.setState({
            selectedBranch: match._id,
            selectedBranchName: match.name,
          });
      }

      // Load surgeries for today
      await this.loadSurgeries();
    } catch (err) {
      console.error("Error loading initial data:", err);
      cogoToast.error("Failed to load initial data");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      // Use the existing branch master API
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});

      // Normalize different response structures to an array of {_id, name}
      const rawBranches = response?.data || response || [];
      const branches = (Array.isArray(rawBranches) ? rawBranches : [])
        .map((site, idx) => ({
          _id:
            site?._id ||
            site?.id ||
            site?.siteId ||
            site?.value ||
            site?.key ||
            (typeof site === "string" ? site : String(idx)),
          name:
            site?.site ||
            site?.siteName ||
            site?.name ||
            site?.branchName ||
            site?.label ||
            (typeof site === "string" ? site : `Site ${idx + 1}`),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      console.log("🔍 DEBUG: Branches loaded:", branches.length);
      this.setState({ branches });
      return branches;
    } catch (err) {
      console.error("Error loading branches:", err);
      // Try alternative API endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`, {});
        const rawBranches = response?.data || response || [];
        const branches = (Array.isArray(rawBranches) ? rawBranches : [])
          .map((site, idx) => ({
            _id: site?._id || site?.id || String(idx),
            name: site?.site || site?.siteName || site?.name || site?.branchName || site?.label || `Site ${idx + 1}`,
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.setState({ branches });
        return branches;
      } catch (fallbackErr) {
        console.error("Fallback branch API also failed:", fallbackErr);
        cogoToast.error("Failed to load branches");
        return [];
      }
    }
  };

  loadSurgeries = async () => {
    try {
      this.setState({ isGenerating: true });

      const startOfDay = moment(this.state.fromDate).startOf("day");
      const endOfDay = moment(this.state.toDate).endOf("day");

      const payload = {
        page: 1,
        sortBy: "scheduledDate",
        sortOrder: "asc",
        startDate: startOfDay.format("YYYY-MM-DD"),
        endDate: endOfDay.format("YYYY-MM-DD"),
        dateField: "scheduledDate",
        // Send explicit timestamps to avoid server-side midnight timezone drift
        startDateTime: startOfDay.toISOString(),
        endDateTime: endOfDay.toISOString(),
      };

      // Add branch filter if selected
      if (this.state.selectedBranch) {
        payload.branchName = this.state.selectedBranch;
      }

      console.log("🔍 DEBUG: API filters applied:", {
        dateRange: `${payload.startDate} to ${payload.endDate}`,
        branchFilter: this.state.selectedBranch || "All branches",
        statusFilter: this.state.selectedStatus && this.state.selectedStatus.length > 0 ? this.state.selectedStatus.join(", ") : "All statuses",
        isBilledFilter: this.state.selectedIsBilled !== "" ? (this.state.selectedIsBilled === "true" ? "Billed" : "Not Billed") : "All",
        dateField: payload.dateField,
        startDateTime: payload.startDateTime,
        endDateTime: payload.endDateTime,
      });

      // Use the existing getSurgeries API
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`, payload);

      // Handle the API response structure properly
      const surgeries = response?.data?.items ? response.data.items : response?.data || response || [];

      // Enforce client-side date filtering on scheduledDate (fallbacks) to avoid stale records
      const filteredByDate = (surgeries || []).filter((s) => {
        const dateValue =
          s?.scheduledDate ||
          s?.ScheduledDate || // handle capitalized key just in case
          s?.scheduledAtDate ||
          s?.surgeryDate ||
          s?.scheduledOn ||
          s?.bookingDate ||
          null;
        if (!dateValue) return false;
        const m = moment(dateValue);
        if (!m.isValid()) return false;
        return m.isBetween(startOfDay, endOfDay, "day", "[]");
      });

      // Apply status filter - filter by selected statuses (array)
      // Convert display values (capitalized) to lowercase for filtering
      let statusFiltered = filteredByDate;
      if (this.state.selectedStatus && this.state.selectedStatus.length > 0) {
        const selectedStatusesLower = this.state.selectedStatus.map((s) => String(s).toLowerCase());
        statusFiltered = filteredByDate.filter((s) => {
          const status = String(s?.status || "").toLowerCase();
          return selectedStatusesLower.includes(status);
        });
      }

      // Apply isBilled filter if selected
      if (this.state.selectedIsBilled !== "") {
        const isBilledFilter = this.state.selectedIsBilled === "true";
        statusFiltered = statusFiltered.filter((s) => {
          const isBilled = Boolean(s?.isBilled);
          return isBilled === isBilledFilter;
        });
      }

      console.log("🔍 DEBUG: Anesthetist Report API Response:", response);
      console.log("🔍 DEBUG: Surgeries loaded for report:", surgeries.length);

      // Transform the surgery data to match report format
      const transformedSurgeries = this.transformSurgeryData(statusFiltered);

      // Store all surgeries
      this.setState({
        allSurgeries: transformedSurgeries,
        surgeries: transformedSurgeries,
        reportData: transformedSurgeries,
      });

      cogoToast.success(`Loaded ${transformedSurgeries.length} surgery records`);
    } catch (err) {
      console.error("Error loading surgeries:", err);
      cogoToast.error("Failed to load surgeries");
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  // Helper function to calculate age from date of birth
  calculateAge = (dob) => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    } catch (err) {
      console.error("Error calculating age:", err);
      return null;
    }
  };

  // Helper function to format selected surgeries
  formatSelectedSurgeries = (selectedSurgeries) => {
    if (!selectedSurgeries || !Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
      return null;
    }

    return selectedSurgeries
      .map((surgery) => {
        let surgeryDisplay = surgery.name || "Unknown Surgery";
        return surgeryDisplay;
      })
      .join("; ");
  };

  // Helper function to format selected surgery groups
  formatSelectedGroup = (selectedSurgeries) => {
    if (!selectedSurgeries || !Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
      return null;
    }

    return selectedSurgeries.map((surgery) => surgery.group || "").join(", ");
  };

    // Helper to title-case strings (centralized)
    toTitleCase = (value) => toTitleCaseGlobal(value);

    // Status color meta for cell renderer (centralized)
    getStatusMeta = (status) => getStatusMetaGlobal(status);

    getDisplayStatusMeta = (row) => {
        const status = row?.status || row?.surgeryStatus || row?.workflowStatus || row?.Status || "-";
        const payorPanel = row?.payorPanel || row?.payor || row?.patientPanel || "";
        const isBilled = row?.isBilled ?? false;
        return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
    };

    // Render status as a colored pill
    renderStatusCell = (statusOrRow) => {
        if (!statusOrRow) {
            return "-";
        }
        const meta =
            typeof statusOrRow === "string"
                ? this.getStatusMeta(statusOrRow)
                : this.getDisplayStatusMeta(statusOrRow);
        return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
    };
    renderIsBilledCell = (row) => getIsBilledPill(row);

  // Helper function to get package rate from consolidated billing
  getPackageRate = (consolidatedBilling) => {
    if (!consolidatedBilling) return 0;

    // Extract package rate from consolidated billing (in paise, convert to rupees)
    const rateInPaise = consolidatedBilling.surgeryRatePaise || 0;
    return rateInPaise / 100;
  };

  // Helper function to get anaesthesia charge from consolidated billing
  getAnaesthesiaCharge = (consolidatedBilling) => {
    if (!consolidatedBilling) return 0;

    // Extract anaesthesia charge from consolidated billing (in paise, convert to rupees)
    const chargeInPaise = consolidatedBilling.anaestheticChargesPaise || 0;
    return chargeInPaise / 100;
  };

  // Navigate to surgery details page
  navigateToDetails = (surgery) => {
    const detailsPath = `/v3/workflow/surgery-booking/${surgery._id || surgery.surgeryId || surgery.bookingReference}`;
    window.open(detailsPath, "_blank", "noopener,noreferrer");
  };

  // AG Grid column definitions
  getColumnDefs = () => [
    {
      headerName: "Surgery ID",
      field: "surgeryId",
      width: 140,
      pinned: "left",
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const value = params.value;
        if (!value || value === "-") return "-";

        const container = document.createElement("div");
        const link = document.createElement("span");

        link.innerHTML = value;
        link.style.color = "#137cbd";
        link.style.cursor = "pointer";
        link.style.textDecoration = "underline";
        link.style.fontWeight = "600";
        link.onclick = () => this.navigateToDetails(params.data);

        container.appendChild(link);
        return container;
      },
    },
    {
      headerName: "Branch",
      field: "branchName",
      width: 150,
      filter: "agTextColumnFilter",
      cellStyle: { fontWeight: "500" },
    },
    {
      headerName: "PID/Patient Name",
      field: "patientInfo",
      width: 220,
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        const pid = params.data.patientId || params.data.pid || "-";
        const name = params.data.patientName || "-";
        return `${pid}/${name}`;
      },
      cellRenderer: (params) => {
        const pid = params.data.patientId || params.data.pid || "-";
        const name = params.data.patientName || "-";

        return `
                    <div>
                        <div style="font-weight: 600; margin-bottom: 4px;">${pid} / ${name}</div>
                    </div>
                `;
      },
    },
    {
      headerName: "Surgery Date",
      field: "surgeryDate",
      width: 140,
      filter: "agDateColumnFilter",
      valueGetter: (params) => {
        return params.data.surgeryDate ? moment(params.data.surgeryDate).format("DD/MM/YYYY") : "-";
      },
      cellRenderer: (params) => {
        const date = params.data.surgeryDate ? moment(params.data.surgeryDate).format("DD/MM/YYYY") : "-";
        const time = params.data.scheduledTime || "";

        return `
                    <div style="text-align: center;">
                        <div style="font-weight: 600; margin-bottom: 2px;">${date}</div>
                        ${time ? `<div style="font-size: 12px; color: #8A9BA8;">${time}</div>` : ""}
                    </div>
                `;
      },
      cellStyle: { textAlign: "center", fontWeight: "500" },
    },
    {
      headerName: "Status",
      field: "status",
      width: 130,
      filter: "agSetColumnFilter",
      filterValueGetter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
      valueFormatter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
      cellRenderer: (params) => this.renderStatusCell(params.data),
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Is Billed",
      field: "isBilled",
      width: 120,
      filter: "agSetColumnFilter",
      valueGetter: (params) => getIsBilledLabel(params.data),
      valueFormatter: (params) => getIsBilledLabel(params.data),
      cellRenderer: (params) => this.renderIsBilledCell(params.data),
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Surgery",
      field: "surgeryName",
      width: 300,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const surgery = params.data.surgeryName || params.data.surgery || "-";
        return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 300px;">${surgery}</div>`;
      },
    },
    {
      headerName: "Surgery Group",
      field: "surgeryGroup",
      width: 200,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        return `<div style="line-height: 1.4; word-wrap: break-word; max-width: 200px;">${params.value || "-"}</div>`;
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Doctor/Surgeon",
      field: "doctorName",
      width: 180,
      filter: "agTextColumnFilter",
      valueFormatter: (params) => {
        return params.value ? params.value.replace("DR. ", "") : "-";
      },
      cellStyle: { fontWeight: "600" },
    },
    {
      headerName: "Anaesthetist Name",
      field: "anaesthetist",
      width: 180,
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        return params.data.anaesthetist || "NA";
      },
      cellStyle: { fontWeight: "500" },
    },
    {
      headerName: "Billed By",
      field: "billedBy",
      width: 160,
      filter: "agTextColumnFilter",
      valueGetter: (params) => params.data.billedBy || "-",
      cellStyle: { fontWeight: "500" },
    },
    {
      headerName: "Package Rate",
      field: "packageRate",
      width: 140,
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        const value = params.value || 0;
        return value > 0 ? `₹${value.toLocaleString("en-IN")}` : "-";
      },
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Anaesthesia Charge",
      field: "anaesthesiaCharge",
      width: 160,
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        const value = params.value || 0;
        return value > 0 ? `₹${value.toLocaleString("en-IN")}` : "-";
      },
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Anesthesia",
      field: "anesthesia",
      width: 180,
      filter: "agTextColumnFilter",
      valueGetter: (params) => {
        return params.data.anesthesia || "-";
      },
      cellStyle: { fontWeight: "500" },
    },
  ];

  // Transform surgery booking data to report format
  transformSurgeryData = (surgeries) => {
    return surgeries.map((surgery, index) => {
      const scheduledDateValue =
        surgery.scheduledDate ||
        surgery.ScheduledDate ||
        surgery.scheduledAtDate ||
        surgery.surgeryDate ||
        surgery.scheduledOn ||
        surgery.bookingDate ||
        null;

      return {
        _id: surgery._id || surgery.id || `surgery_${index}`,
        surgeryId: surgery.bookingReference || surgery._id?.toString() || `BKP25U${String(index).padStart(5, "0")}`,
        patientId:
          surgery.patientGenericPid ||
          surgery.patientId ||
          surgery.patientOriginalData?.sysPid ||
          `P${String(index).padStart(10, "0")}`,
        patientName: surgery.patientName || surgery.patientOriginalData?.name || "Unknown Patient",
        surgeryDate: scheduledDateValue ? new Date(scheduledDateValue) : null,
        scheduledTime: surgery.scheduledTime || surgery.surgeryTime || surgery.scheduledAtTime || "09:00",
        surgeryName:
          this.formatSelectedSurgeries(surgery.selectedSurgeries) ||
          (Array.isArray(surgery.surgeryNames) && surgery.surgeryNames.length > 0
            ? surgery.surgeryNames.join(", ")
            : typeof surgery.surgeryNames === "string" && surgery.surgeryNames.trim()
            ? surgery.surgeryNames
            : surgery.surgeryName) ||
          surgery.procedure ||
          surgery.surgeryType ||
          surgery.type ||
          "Surgery",
          surgeryGroup: this.formatSelectedGroup(surgery.selectedSurgeries) || surgery.group || surgery.payor || "",
        doctorName: surgery.doctorName || "Unknown Doctor",
        doctorId: surgery.doctorId || surgery.doctor?._id || "",
        anaesthetist: surgery.anaesthetist || surgery.anaesthetistName || surgery.anesthesiologist || null,
        billedBy:
          surgery.doneBy ||
          surgery.billedBy ||
          surgery.user ||
          surgery.createdByName ||
          surgery.createdUserName ||
          (surgery.createdBy && (surgery.createdBy.name || surgery.createdBy.username || surgery.createdBy.displayName)) ||
          null,
        packageRate: this.getPackageRate(surgery.consolidatedBilling),
        anaesthesiaCharge: this.getAnaesthesiaCharge(surgery.consolidatedBilling),
        anesthesia: surgery.anesthesia || surgery.anaesthesia || surgery.anesthesiaType || null,
        branchName: surgery.branchName || "Unknown Branch",
        branchId: surgery.branchId || surgery.branch?._id || "",
        status: surgery.status || "confirmed",
        payorPanel: surgery.payorPanel || surgery.payor || surgery.patientPanel || "",
        isBilled: Boolean(surgery.isBilled),
        statusMeta: this.getDisplayStatusMeta({
            status: surgery.status,
            payorPanel: surgery.payorPanel || surgery.payor || surgery.patientPanel || "",
            isBilled: surgery.isBilled,
        }).label,
      };
    });
  };

  // Helper function to create branch items for dropdown
  createBranchSelectItems = () => {
    return this.state.branches.map((branch) => branch.name).filter(Boolean);
  };

  // AG Grid event handlers
  onGridReady = (params) => {
    this.setState({
      gridApi: params.api,
      gridColumnApi: params.columnApi,
    });
  };

  // Get only visible/displayed columns (respects sidebar column selection)
  getDisplayedColumns = () => {
    if (!this.state.gridColumnApi) return this.getColumnDefs();
    const allColumns = this.state.gridColumnApi.getAllColumns();
    if (!allColumns) return this.getColumnDefs();
    const visibleColumns = allColumns.filter((col) => col.isVisible());
    return visibleColumns.map((col) => {
      const colDef = col.getColDef();
      return {
        headerName: colDef.headerName,
        field: colDef.field,
        ...colDef,
      };
    });
  };

  onQuickFilterChanged = (event) => {
    this.setState({ quickFilterText: event.target.value });
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date });
  };

  // Handle branch selection from SingleSelectWrapper
  handleBranchFilterSelect = (branchName) => {
    const selectedBranch = this.state.branches.find((branch) => branch.name === branchName);
    this.setState({
      selectedBranch: selectedBranch ? selectedBranch._id : "",
      selectedBranchName: branchName || null,
    });
  };

  generateReport = async () => {
    await this.loadSurgeries();
  };

  // Helper function to get sorted and filtered data from AG Grid
  getSortedAndFilteredData = () => {
    if (this.state.gridApi) {
      const sortedData = [];
      // Use forEachNodeAfterFilterAndSort to get data in the current sort order
      this.state.gridApi.forEachNodeAfterFilterAndSort((node) => {
        sortedData.push(node.data);
      });
      return sortedData;
    }
    // Fallback to state data if grid API not available
    return this.state.surgeries;
  };

  exportToCSV = () => {
    // Get sorted and filtered data
    const filteredData = this.getSortedAndFilteredData();

    // Get only visible columns (respects sidebar column selection)
    const columns = this.getDisplayedColumns();

    const selectedBranchName =
      this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";

    // Create comprehensive header information
    const headerInfo = [
      [`ANAESTHETIST REPORT - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}`],
      [""], // Empty row for spacing
      ["Report Details:"],
      [`Branch: ${selectedBranchName}`],
      [
        `Period: ${moment(this.state.fromDate).format("DD-MMM-YYYY")} to ${moment(this.state.toDate).format(
          "DD-MMM-YYYY"
        )}`,
      ],
      [`Generated At: ${moment().format("DD-MMM-YYYY, HH:mm:ss")}`],
      [`Total Records: ${filteredData.length}`],
      [""], // Empty row for spacing
      ["Data Export:"],
      [""], // Empty row for spacing
    ];

    // Create column headers from visible columns
    const headers = columns.map((col) => col.headerName);

    // Format data rows based on visible columns
    const dataRows = filteredData.map((surgery) => {
      return columns.map((col) => {
        const field = col.field;

        // Handle special field mappings
        if (field === "surgeryId") {
          return surgery.surgeryId || "-";
        }
        if (field === "patientInfo") {
          const pid = surgery.patientId || surgery.pid || "-";
          const name = surgery.patientName || "-";
          return `${pid}/${name}`;
        }
        if (field === "surgeryDate") {
          return surgery.surgeryDate
            ? `${moment(surgery.surgeryDate).format("DD/MM/YYYY")}${
                surgery.scheduledTime ? ` ${surgery.scheduledTime}` : ""
              }`
            : "-";
        }
        if (field === "doctorName") {
          return surgery.doctorName ? surgery.doctorName.replace("DR. ", "") : "-";
        }
        if (field === "anaesthetist") {
          return surgery.anaesthetist || "NA";
        }
        if (field === "status") {
          return this.getDisplayStatusMeta(surgery).label;
        }
        if (field === "packageRate") {
          const value = surgery.packageRate || 0;
          return value > 0 ? value : "-";
        }
        if (field === "anaesthesiaCharge") {
          const value = surgery.anaesthesiaCharge || 0;
          return value > 0 ? value : "-";
        }
        if (field === "anesthesia") {
          return surgery.anesthesia || "-";
        }

        // Default: return field value or dash
        return surgery[field] || "-";
      });
    });

    // Combine all data
    const csvData = [...headerInfo, headers, ...dataRows];

    // Convert to CSV format
    const csvContent = csvData
      .map((row) =>
        row
          .map((field) => {
            // Escape quotes and wrap in quotes if field contains comma, quote, or newline
            const fieldStr = String(field || "");
            if (fieldStr.includes(",") || fieldStr.includes('"') || fieldStr.includes("\n")) {
              return `"${fieldStr.replace(/"/g, '""')}"`;
            }
            return fieldStr;
          })
          .join(",")
      )
      .join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    const fileName = `Anaesthetist_Report_${moment(this.state.fromDate).format("DD-MMM-YYYY")}_to_${moment(
      this.state.toDate
    ).format("DD-MMM-YYYY")}_Generated_${moment().format("DD-MMM-YYYY_HH-mm")}.csv`;
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    cogoToast.success(`CSV exported successfully: ${fileName} (with selected columns)`);
  };

  exportToPDF = () => {
    try {
      // Get sorted and filtered data from AG Grid
      const filteredData = this.getSortedAndFilteredData();

      // Get only visible columns (respects sidebar column selection)
      const columns = this.getDisplayedColumns();

      const selectedBranchName =
        this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";

      // Create a new window for printing
      const printWindow = window.open("", "_blank");

      // Generate HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Anaesthetist Report</title>
            <style>
                @page {
                    size: A4 landscape;
                    margin: 8mm;
                }
                body { 
                    font-family: Arial, sans-serif; 
                    margin: 10px; 
                    font-size: 14px;
                    color: #000;
                }
                .header {
                    text-align: center;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #000;
                    padding-bottom: 20px;
                }
                .header h1 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: bold;
                }
                .header p {
                    margin: 5px 0;
                    font-size: 16px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                    font-size: 12px;
                }
                th, td {
                    border: 1px solid #000;
                    text-align: left;
                    vertical-align: top;
                }
                th { 
                    padding: 16px 10px;
                    text-align: center;
                }
                td { 
                    padding: 8px 10px;
                    line-height: 1.6; 
                }
                tbody tr { page-break-inside: avoid; }
                th {
                    background-color: #f0f0f0;
                    font-weight: bold;
                    text-align: center;
                }
                .no-break {
                    page-break-inside: avoid;
                }
                @media print {
                    @page { size: A4 landscape; margin: 8mm; }
                    body { margin: 5px; }
                    .header { page-break-after: avoid; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>ANAESTHETIST REPORT - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}</h1>
                <p>${selectedBranchName} - ${moment().format("DD/MM/YYYY, HH:mm")}</p>
                <p>Period: ${moment(this.state.fromDate).format("DD-MMM-YYYY")} to ${moment(this.state.toDate).format(
        "DD-MMM-YYYY"
      )}</p>
                <p>Total Records: ${filteredData.length}</p>
            </div>
            
            <table>
                <thead>
                    <tr>
                        ${columns.map((col) => `<th>${col.headerName}</th>`).join("")}
                    </tr>
                </thead>
                <tbody>
                    ${filteredData
                      .map((surgery) => {
                        const cells = columns
                          .map((col) => {
                            const field = col.field;

                            // Handle special field mappings
                            if (field === "surgeryId") {
                              return `<td>${surgery.surgeryId || "-"}</td>`;
                            }
                            if (field === "patientInfo") {
                              return `<td><strong>${surgery.patientId || surgery.pid || "-"}/<br>${
                                surgery.patientName || "-"
                              }</strong></td>`;
                            }
                            if (field === "surgeryDate") {
                              return `<td>${
                                surgery.surgeryDate
                                  ? `${moment(surgery.surgeryDate).format("DD/MM/YY")}${
                                      surgery.scheduledTime ? `<br><small>${surgery.scheduledTime}</small>` : ""
                                    }`
                                  : "-"
                              }</td>`;
                            }
                            if (field === "surgeryName") {
                              return `<td>${surgery.surgeryName || surgery.surgery || "-"}</td>`;
                            }
                            if (field === "doctorName") {
                              return `<td>${surgery.doctorName ? surgery.doctorName.replace("DR. ", "") : "-"}</td>`;
                            }
                            if (field === "anaesthetist") {
                              return `<td>${surgery.anaesthetist || "NA"}</td>`;
                            }
                            if (field === "status") {
                              return `<td>${this.getDisplayStatusMeta(surgery).label}</td>`;
                            }
                            if (field === "packageRate") {
                              const value = surgery.packageRate || 0;
                              return `<td style="text-align: right;">${
                                value > 0 ? `₹${value.toLocaleString("en-IN")}` : "-"
                              }</td>`;
                            }
                            if (field === "anaesthesiaCharge") {
                              const value = surgery.anaesthesiaCharge || 0;
                              return `<td style="text-align: right;">${
                                value > 0 ? `₹${value.toLocaleString("en-IN")}` : "-"
                              }</td>`;
                            }
                            if (field === "anesthesia") {
                              return `<td>${surgery.anesthesia || "-"}</td>`;
                            }

                            // Default: return field value or dash
                            return `<td>${surgery[field] || "-"}</td>`;
                          })
                          .join("");
                        return `<tr class="no-break">${cells}</tr>`;
                      })
                      .join("")}
                </tbody>
            </table>
        </body>
        </html>
        `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then trigger print
      printWindow.onload = () => {
        printWindow.print();
      };

      cogoToast.success("PDF print dialog opened (with selected columns)");
    } catch (err) {
      console.error("Error exporting PDF:", err);
      cogoToast.error("Failed to export PDF");
    }
  };

  renderReportHeader = () => {
    const selectedBranchName =
      this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";

    return (
      <div
        style={{
          textAlign: "center",
          marginBottom: "30px",
          borderBottom: "2px solid var(--border-color)",
          paddingBottom: "20px",
        }}
      >
        <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
          ANAESTHETIST REPORT - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
        </H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "5px 0",
            fontSize: "16px",
          }}
        >
          {selectedBranchName} - {moment().format("DD/MM/YYYY, HH:mm")}
        </Text>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "5px 0",
            fontSize: "14px",
          }}
        >
          Period: {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
          {moment(this.state.toDate).format("DD-MMM-YYYY")}
        </Text>
      </div>
    );
  };

  renderFilters = () => {
    return (
      <Card
        elevation={2}
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <Icon icon="filter" style={{ marginRight: "10px", color: "var(--text-primary)" }} />
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Report Filters</H3>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Tag intent="success" large>
              {this.state.surgeries.length} Records
            </Tag>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexWrap: "wrap",
            marginBottom: "15px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              From Date *
            </Tag>
            <DateInput
              value={this.state.fromDate}
              onChange={(date) => this.handleDateChange("fromDate", date)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="From Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              To Date *
            </Tag>
            <DateInput
              value={this.state.toDate}
              onChange={(date) => this.handleDateChange("toDate", date)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="To Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Branch
            </Tag>
            <div style={{ width: "160px" }}>
              <SingleSelectWrapper
                items={[`All Branches (${this.state.branches.length})`, ...this.createBranchSelectItems()]}
                selectedItem={this.state.selectedBranchName || `All Branches (${this.state.branches.length})`}
                placeholder="Search branches..."
                onChange={(value) => {
                  if (value === `All Branches (${this.state.branches.length})`) {
                    this.handleBranchFilterSelect(null);
                  } else {
                    this.handleBranchFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Status
            </Tag>
            <div style={{ width: "200px" }}>
              <MultiSelectWrapper
                items={["Confirmed", "Discharged", "Cancelled", "Postponed"]}
                selectedItems={this.state.selectedStatus || []}
                onChange={(selectedItems) => {
                  this.setState({ selectedStatus: selectedItems || [] });
                }}
                placeholder="Select statuses..."
                fill
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Is Billed
            </Tag>
            <HTMLSelect
              value={this.state.selectedIsBilled}
              onChange={(e) => this.setState({ selectedIsBilled: e.target.value })}
              style={{ width: "140px" }}
            >
              <option value="">All</option>
              <option value="true">Billed</option>
              <option value="false">Not Billed</option>
            </HTMLSelect>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
            flexWrap: "nowrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Quick Filter
            </Tag>
            <InputGroup
              placeholder="Search all columns..."
              value={this.state.quickFilterText}
              onChange={this.onQuickFilterChanged}
              leftIcon="search"
              style={{ width: "200px" }}
              small
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button intent="primary" icon="search" onClick={this.generateReport} loading={this.state.isGenerating}>
              Generate
            </Button>

            <Button
              icon="document"
              onClick={this.exportToPDF}
              disabled={this.state.surgeries.length === 0}
              title="Export to PDF"
            >
              PDF
            </Button>

            <Button
              icon="th"
              onClick={this.exportToCSV}
              disabled={this.state.surgeries.length === 0}
              title="Export to CSV"
            >
              CSV
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  renderDataTable = () => {
    if (!this.state.surgeries || this.state.surgeries.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-secondary)",
            backgroundColor: "var(--bg-secondary)",
            borderRadius: "8px",
            border: "1px solid var(--border-color)",
          }}
        >
          <Icon icon="th" size={48} style={{ marginBottom: "15px", opacity: 0.5 }} />
          <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>No records found</div>
          <div style={{ fontSize: "14px", opacity: 0.8 }}>Try adjusting your date range or filters</div>
        </div>
      );
    }

    return (
      <div
        className="ag-theme-alpine-dark"
        style={{
          height: "600px",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <AgGridReact
          rowData={this.state.surgeries}
          columnDefs={this.getColumnDefs()}
          onGridReady={this.onGridReady}
          quickFilterText={this.state.quickFilterText}
          animateRows={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableBrowserTooltips={true}
          enableRangeSelection={true}
          enableCellTextSelection={true}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressColumnFilter: false,
                  suppressColumnSelectAll: false,
                  suppressColumnExpandAll: false,
                },
              },
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
                toolPanelParams: {
                  suppressExpandAll: false,
                  suppressFilterSearch: false,
                },
              },
            ],
            defaultToolPanel: null,
            hiddenByDefault: false,
            position: "right",
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 80,
            menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
          }}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
          domLayout="normal"
          getRowId={(params) => params.data._id || params.data.surgeryId}
          rowHeight={60}
          headerHeight={45}
        />
      </div>
    );
  };

  renderReportContent = () => {
    if (this.state.isGenerating) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Generating Report..."
          description="Please wait while we fetch the surgery data"
        />
      );
    }

    if (this.state.surgeries.length === 0) {
      return (
        <NonIdealState
          icon="th"
          title="No Records Found"
          description="No surgery records found for the selected criteria. Try adjusting your filters."
        />
      );
    }

    return (
      <div>
        {this.renderReportHeader()}
        {this.renderDataTable()}
      </div>
    );
  };

  render() {
    if (this.state.isLoading) {
      return <NonIdealState icon={<Spinner />} title="Loading..." description="Preparing filters and data" />;
    }
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Icon icon="th-list" style={{ marginRight: "12px", color: "var(--intent-primary)" }} />
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Anaesthetist Report</H1>
          </div>
        </div>

        {this.renderFilters()}

        <Card
          elevation={2}
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
          }}
        >
          {this.renderReportContent()}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(AnesthetistReport)));
